import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';

// Async thunks
export const fetchBudgets = createAsyncThunk(
  'budgets/fetchBudgets',
  async () => {
    const { data } = await axiosInstance.get('/budgets');
    return data;
  }
);

export const createBudget = createAsyncThunk(
  'budgets/createBudget',
  async (budgetData) => {
    const { data } = await axiosInstance.post('/budgets', budgetData);
    return data;
  }
);

export const updateBudget = createAsyncThunk(
  'budgets/updateBudget',
  async ({ id, ...budgetData }) => {
    const { data } = await axiosInstance.put(`/budgets/${id}`, budgetData);
    return data;
  }
);

export const deleteBudget = createAsyncThunk(
  'budgets/deleteBudget',
  async (id) => {
    await axiosInstance.delete(`/budgets/${id}`);
    return id;
  }
);

export const fetchBudgetAnalytics = createAsyncThunk(
  'budgets/fetchAnalytics',
  async (budgetId) => {
    const { data } = await axiosInstance.get(`/budgets/${budgetId}/analytics`);
    return data;
  }
);

const initialState = {
  budgets: [],
  analytics: {
    totalBudget: 0,
    totalSpent: 0,
    totalRemaining: 0,
    budgetUtilization: 0,
    categoryPerformance: [],
    monthlyTrends: [],
    alerts: [],
  },
  loading: {
    budgets: false,
    creating: false,
    updating: false,
    deleting: false,
    analytics: false,
  },
  error: null,
  selectedBudget: null,
  alertSettings: {
    enabled: true,
    thresholds: {
      warning: 75, // 75% of budget
      critical: 90, // 90% of budget
    },
    notifications: {
      email: true,
      push: true,
      inApp: true,
    },
  },
};

const budgetSlice = createSlice({
  name: 'budgets',
  initialState,
  reducers: {
    setSelectedBudget: (state, action) => {
      state.selectedBudget = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateAlertSettings: (state, action) => {
      state.alertSettings = { ...state.alertSettings, ...action.payload };
    },
    addBudgetAlert: (state, action) => {
      state.analytics.alerts.push(action.payload);
    },
    removeBudgetAlert: (state, action) => {
      state.analytics.alerts = state.analytics.alerts.filter(
        alert => alert.id !== action.payload
      );
    },
    markAlertAsRead: (state, action) => {
      const alert = state.analytics.alerts.find(alert => alert.id === action.payload);
      if (alert) {
        alert.read = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch budgets
      .addCase(fetchBudgets.pending, (state) => {
        state.loading.budgets = true;
        state.error = null;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.loading.budgets = false;
        state.budgets = action.payload;
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.loading.budgets = false;
        state.error = action.error.message;
      })
      
      // Create budget
      .addCase(createBudget.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createBudget.fulfilled, (state, action) => {
        state.loading.creating = false;
        state.budgets.push(action.payload);
      })
      .addCase(createBudget.rejected, (state, action) => {
        state.loading.creating = false;
        state.error = action.error.message;
      })
      
      // Update budget
      .addCase(updateBudget.pending, (state) => {
        state.loading.updating = true;
        state.error = null;
      })
      .addCase(updateBudget.fulfilled, (state, action) => {
        state.loading.updating = false;
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index] = action.payload;
        }
      })
      .addCase(updateBudget.rejected, (state, action) => {
        state.loading.updating = false;
        state.error = action.error.message;
      })
      
      // Delete budget
      .addCase(deleteBudget.pending, (state) => {
        state.loading.deleting = true;
        state.error = null;
      })
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.loading.deleting = false;
        state.budgets = state.budgets.filter(b => b.id !== action.payload);
      })
      .addCase(deleteBudget.rejected, (state, action) => {
        state.loading.deleting = false;
        state.error = action.error.message;
      })
      
      // Fetch analytics
      .addCase(fetchBudgetAnalytics.pending, (state) => {
        state.loading.analytics = true;
      })
      .addCase(fetchBudgetAnalytics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics = { ...state.analytics, ...action.payload };
      })
      .addCase(fetchBudgetAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setSelectedBudget,
  clearError,
  updateAlertSettings,
  addBudgetAlert,
  removeBudgetAlert,
  markAlertAsRead,
} = budgetSlice.actions;

// Selectors
export const selectBudgets = (state) => state.budgets.budgets;
export const selectBudgetAnalytics = (state) => state.budgets.analytics;
export const selectBudgetLoading = (state) => state.budgets.loading;
export const selectBudgetError = (state) => state.budgets.error;
export const selectSelectedBudget = (state) => state.budgets.selectedBudget;
export const selectAlertSettings = (state) => state.budgets.alertSettings;

// Advanced selectors
export const selectActiveBudgets = (state) => {
  return state.budgets.budgets.filter(budget => budget.active);
};

export const selectBudgetAlerts = (state) => {
  return state.budgets.analytics.alerts.filter(alert => !alert.read);
};

export const selectBudgetPerformance = (state) => {
  const budgets = state.budgets.budgets;
  return budgets.map(budget => {
    const utilization = budget.utilization_percentage || 0;
    const spentAmount = budget.current_spent || 0;
    const budgetAmount = budget.budget_amount || 0;
    
    let status = 'good';
    if (utilization > 100) {
      status = 'critical';
    } else if (utilization > 80) {
      status = 'warning';
    }
    
    return {
      ...budget,
      utilization: Math.min(utilization, 100), // Cap at 100 for progress bar
      status,
      spent_amount: spentAmount,
      amount: budgetAmount,
      remaining: Math.max(budgetAmount - spentAmount, 0),
      isOverBudget: utilization > 100,
    };
  });
};

export const selectBudgetPerformanceStats = (state) => {
  const budgets = state.budgets.budgets;
  const performance = {
    onTrack: 0,
    overBudget: 0,
    underBudget: 0,
    totalBudgets: budgets.length
  };

  budgets.forEach(budget => {
    const utilization = budget.utilization_percentage || 0;
    if (utilization > 100) {
      performance.overBudget++;
    } else if (utilization > 80) {
      performance.onTrack++;
    } else {
      performance.underBudget++;
    }
  });

  return performance;
};

export const selectOverallBudgetHealth = (state) => {
  const budgets = state.budgets.budgets;
  if (budgets.length === 0) return 'good';

  const overBudgetCount = budgets.filter(b => (b.utilization_percentage || 0) > 100).length;
  const warningCount = budgets.filter(b => {
    const util = b.utilization_percentage || 0;
    return util > 80 && util <= 100;
  }).length;

  const overBudgetRatio = overBudgetCount / budgets.length;
  const warningRatio = warningCount / budgets.length;

  if (overBudgetRatio > 0.3) return 'poor';
  if (overBudgetRatio > 0.1 || warningRatio > 0.5) return 'warning';
  return 'good';
};

export default budgetSlice.reducer; 